"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { EquationPanel } from "@/components/tools/chem-physical/arrhenius-equation-panel";
import {
  PlotPanel,
  RisePanel,
} from "@/components/tools/chem-physical/arrhenius-more-panels";
import { TwoPointPanel } from "@/components/tools/chem-physical/arrhenius-two-point-panel";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const ARRHENIUS_EQUATION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeEquation: "k, A, Ea or T from k = Ae^(−Ea/RT)",
  modeTwoPoint: "Ea from two temperatures",
  modeRise: "Rate change with temperature",
  modePlot: "Arrhenius plot from data",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  solvedCell: "Solved below",
  tryLabel: "Try an example",
  termK: "Rate constant (k)",
  termA: "Pre-exponential factor (A)",
  termEa: "Activation energy (Ea)",
  termT: "Temperature (T)",
  termK1: "Rate constant at T₁ (k₁)",
  termT1: "First temperature (T₁)",
  termK2: "Rate constant at T₂ (k₂)",
  termT2: "Second temperature (T₂)",
  termFactor: "Rate factor (k₂ ÷ k₁)",
  temperatureRise: "Temperature rise (ΔT)",
  unitEa: "Activation energy unit",
  unitTemperature: "Temperature unit",
  kUnit: "Unit of k (label only)",
  kUnitHint:
    "Shown after k and A in the results; A has the same unit as k. Nothing is converted.",
  kHint: "Any positive number; A must use the same unit.",
  aHint: "In the same unit as k.",
  factorHint: "For example 2 for a doubling.",
  riseHint:
    "In degrees of the temperature unit chosen: a 10 °C rise is a 10 K rise.",
  exponent: "Ea ÷ RT",
  fractionEnergetic: "Fraction with enough energy",
  equation: "k = A × e^(−Ea ÷ RT)",
  rearrangedA: "A = k × e^(Ea ÷ RT)",
  rearrangedEa: "Ea = −RT × ln(k ÷ A)",
  rearrangedT: "T = −Ea ÷ (R × ln(k ÷ A))",
  convertLine: "{label}: {value} {unit} = {converted}",
  substitutedK:
    "k = {a} × e^(−{ea} ÷ ({r} × {t})) = {a} × e^(−{exp}) = {result}",
  substitutedA: "A = {k} × e^({ea} ÷ ({r} × {t})) = {k} × e^({exp}) = {result}",
  substitutedEa: "Ea = −{r} × {t} × ln({k} ÷ {a}) = {result}",
  substitutedT: "T = −{ea} ÷ ({r} × ln({k} ÷ {a})) = {result}",
  answerLine: "{result} = {answer}",
  lnRatio: "ln(k₂ ÷ k₁)",
  negativeEaNote:
    "Ea is negative: the rate constant fell as the temperature rose. That is rare, so check that each temperature is paired with its own k.",
  twoEquation: "ln(k₂ ÷ k₁) = (Ea ÷ R)(1 ÷ T₁ − 1 ÷ T₂)",
  twoRearrangedEa: "Ea = R × ln(k₂ ÷ k₁) ÷ (1 ÷ T₁ − 1 ÷ T₂)",
  twoRearrangedK2: "k₂ = k₁ × e^[(Ea ÷ R)(1 ÷ T₁ − 1 ÷ T₂)]",
  twoRearrangedT2: "1 ÷ T₂ = 1 ÷ T₁ − R × ln(k₂ ÷ k₁) ÷ Ea",
  twoSubstitutedEa:
    "Ea = {r} × ln({k2} ÷ {k1}) ÷ (1 ÷ {t1} − 1 ÷ {t2}) = {result}",
  twoSubstitutedK2:
    "k₂ = {k1} × e^[({ea} ÷ {r}) × (1 ÷ {t1} − 1 ÷ {t2})] = {result}",
  twoSubstitutedT2:
    "1 ÷ T₂ = 1 ÷ {t1} − {r} × ln({k2} ÷ {k1}) ÷ {ea}, so T₂ = {result}",
  preExponentialLine: "A = k₁ × e^(Ea ÷ RT₁) = {a}",
  exampleN2O5: "N₂O₅ decomposition, 650 K and 700 K",
  exampleHi: "HI decomposition data",
  riseEquation: "k₂ ÷ k₁ = e^[(Ea ÷ R)(1 ÷ T − 1 ÷ (T + ΔT))]",
  riseRearrangedEa: "Ea = R × ln(k₂ ÷ k₁) ÷ (1 ÷ T − 1 ÷ (T + ΔT))",
  riseSubstitutedFactor:
    "k₂ ÷ k₁ = e^[({ea} ÷ {r}) × (1 ÷ {t} − 1 ÷ {t2})] = {result}",
  riseSubstitutedEa:
    "Ea = {r} × ln({factor}) ÷ (1 ÷ {t} − 1 ÷ {t2}) = {result}",
  perTenDegrees: "Factor per 10 K",
  riseTableHeading: "Rate factor for this temperature rise",
  columnEa: "Activation energy",
  columnFactor: "Rate factor",
  dataLabel: "Temperature and rate constant, one pair per line",
  dataHint:
    "Separate with a space, comma or tab, for example 555 3.52e-7. The temperature unit is set on the right.",
  rSquared: "R²",
  twoPointsNote:
    "Two points always lie on a straight line, so R² = 1 says nothing about quality. Use at least three temperatures to judge the fit.",
  columnT: "T (K)",
  columnK: "k",
  columnInverseT: "1/T (K⁻¹)",
  columnLnK: "ln k",
  fitEquation:
    "ln k = ln A − (Ea ÷ R)(1 ÷ T), a straight line of ln k against 1 ÷ T",
  fitSlope: "Least-squares line: slope = {slope} K, intercept = {intercept}",
  fitEa: "Ea = −slope × R = −({slope}) × {r} = {ea} J/mol",
  fitA: "A = e^(intercept) = e^({intercept}) = {a}",
  plotLabel: "Arrhenius plot of ln k against 1000 divided by T",
  plotX: "1000 ÷ T (K⁻¹)",
  plotY: "ln k",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorBelowZero:
    "{label} is at or below absolute zero (0 K, −273.15 °C, −459.67 °F), which is impossible.",
  errorNegativeEa:
    "Activation energy cannot be negative here. Check the sign and the order of the data.",
  errorRateAboveA:
    "k cannot be larger than A: e^(−Ea/RT) is at most 1, so A is the upper limit of k.",
  errorSameTemperature:
    "The two temperatures are equal, so they carry no information about Ea. Use two different temperatures.",
  errorOutOfRange:
    "The result is too large or too small to represent. Check the units and the size of the numbers.",
  errorNoTemperature:
    "No positive temperature fits these values. Check Ea, k and A.",
  errorTooFewPoints: "Enter at least two lines of data.",
  errorBadLine:
    "Line {line} should be a temperature and a positive rate constant, separated by a space, comma or tab.",
  errorAllSameTemperature:
    "All the temperatures are the same, so no line can be fitted.",
  errorZeroEa:
    "Ea is zero, so k does not depend on temperature and T₂ cannot be found.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

type Mode = "equation" | "twoPoint" | "rise" | "plot";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function ArrheniusEquationCalculatorWidget() {
  const strings = useToolUi(ARRHENIUS_EQUATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("equation");
  const [sigChoice, setSigChoice] = useState("match");
  const panels = {
    equation: <EquationPanel sigChoice={sigChoice} strings={strings} />,
    twoPoint: <TwoPointPanel sigChoice={sigChoice} strings={strings} />,
    rise: <RisePanel sigChoice={sigChoice} strings={strings} />,
    plot: <PlotPanel sigChoice={sigChoice} strings={strings} />,
  };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="arr-mode"
        onChange={setMode}
        options={[
          { value: "equation", label: strings.modeEquation },
          { value: "twoPoint", label: strings.modeTwoPoint },
          { value: "rise", label: strings.modeRise },
          { value: "plot", label: strings.modePlot },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="arr-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {panels[mode]}
    </>
  );
}
